import React, { useRef, useState } from "react";
import {
  CameraOutlined,
  DeleteOutlined,
  PictureOutlined,
  UploadOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import "./appUpload.scss";

export type AppUploadKind = "image" | "video" | "file";

export interface AppUploadProps {
  kind?: AppUploadKind;
  /** image only: round 96px avatar with a camera badge instead of the 180px cover box */
  avatar?: boolean;
  /** file kind: render a compact "Attach" button instead of the dashed drop area */
  compact?: boolean;
  /** saved value from the server (used until the user picks something new) */
  image?: string | null;
  /** locally picked preview: null/undefined = untouched, "" = removed */
  uploadedImage?: string | null;
  /** name of the chosen file (file kind) */
  fileName?: string | null;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemove?: () => void;
  accept?: string;
  label?: string;
  hint?: string;
  multiple?: boolean;
  disabled?: boolean;
}

const DEFAULTS: Record<AppUploadKind, { accept: string; label: string; hint: string }> = {
  image: { accept: "image/*", label: "Upload image", hint: "Click to browse" },
  video: { accept: "video/*", label: "Upload video", hint: "Click to browse" },
  file: { accept: "*/*", label: "Upload file", hint: "Click to browse" },
};

// The one upload control for the whole app. Same box, same states, same Change / Remove actions.
const AppUpload: React.FC<AppUploadProps> = ({
  kind = "image",
  avatar = false,
  compact = false,
  image,
  uploadedImage,
  fileName,
  handleFileUpload,
  handleRemove,
  accept,
  label,
  hint,
  multiple,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const d = DEFAULTS[kind];
  const shownName = fileName === undefined ? picked : fileName;
  const preview = uploadedImage === null || uploadedImage === undefined ? image : uploadedImage;
  const filled = kind === "file" ? !!shownName : !!preview;
  const open = () => !disabled && inputRef.current?.click();

  const input = (
    <input
      ref={inputRef}
      className="app-upload__input"
      type="file"
      accept={accept ?? d.accept}
      multiple={multiple}
      disabled={disabled}
      onChange={(e) => {
        setPicked(e.target.files?.[0]?.name ?? null);
        handleFileUpload(e);
      }}
    />
  );

  if (kind === "file" && compact) {
    return (
      <div className="app-upload app-upload--compact">
        <button type="button" className="app-upload__btn" onClick={open} disabled={disabled}>
          <UploadOutlined /> {label ?? "Attach a file"}
        </button>
        {input}
      </div>
    );
  }

  if (kind === "image" && avatar) {
    return (
      <div className="app-upload app-upload--avatar">
        <button type="button" className="app-upload__avatar" onClick={open} disabled={disabled} title="Change photo">
          {preview ? <img src={preview} alt="Avatar" /> : <UserOutlined />}
          <span className="app-upload__badge"><CameraOutlined /></span>
        </button>
        {input}
      </div>
    );
  }

  const Icon = kind === "video" ? VideoCameraOutlined : kind === "image" ? PictureOutlined : UploadOutlined;

  return (
    <div className={`app-upload app-upload--${kind}${filled ? " app-upload--filled" : ""}`}>
      <div
        className="app-upload__box"
        role="button"
        tabIndex={0}
        onClick={filled && kind !== "file" ? undefined : open}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !filled && open()}
      >
        {kind === "image" && preview ? (
          <img className="app-upload__preview" src={preview} alt="Preview" />
        ) : kind === "video" && preview ? (
          <video className="app-upload__preview" src={preview} controls />
        ) : filled ? (
          <>
            <Icon className="app-upload__icon" />
            <span className="app-upload__title" title={shownName ?? ""}>{shownName}</span>
          </>
        ) : (
          <>
            <Icon className="app-upload__icon" />
            <span className="app-upload__title">{label ?? d.label}</span>
            <span className="app-upload__hint">{hint ?? d.hint}</span>
          </>
        )}
        {filled && (
          <div className={`app-upload__overlay${kind === "image" ? "" : " app-upload__overlay--corner"}`}>
            <button
              type="button"
              className="app-upload__fab"
              title="Change"
              aria-label="Change"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
            >
              <CameraOutlined />
            </button>
            {handleRemove && (
              <button
                type="button"
                className="app-upload__fab app-upload__fab--danger"
                title="Remove"
                aria-label="Remove"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                <DeleteOutlined />
              </button>
            )}
          </div>
        )}
      </div>
      {input}
    </div>
  );
};

export default AppUpload;
